<template>
  <div>
    <div
      v-for="(fields, module) in fieldsSource"
      :key="module"
      class="textCard__module"
    >
      <div v-for="fieldName in fields" :key="fieldName" class="textCard__field">
        <template v-if="modules[module][fieldName].type === 'text'">
          <v-textarea
            v-for="lang in langsListFor(module, fieldName)"
            :key="module + '.' + fieldName + '.' + lang"
            v-model="structure[module][fieldName].models[lang]"
            class="textCard__textarea"
            :prepend-icon="prepend(module, fieldName, lang)"
            :rules="textareaRules(module, fieldName)"
            :label="label(module, fieldName, lang)"
            :rows="textareaRows(module, fieldName)"
            :hint="hint(module, fieldName, lang)"
          />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'string'">
          <string-field
            v-for="lang in langsListFor(module, fieldName)"
            :key="module + '.' + fieldName + '.' + lang"
            v-model="structure[module][fieldName].models[lang]"
            :prepend-icon="prepend(module, fieldName, lang)"
            :rules="modules[module][fieldName].rules"
            :label="label(module, fieldName, lang)"
            :hint="hint(module, fieldName, lang)"
            :textarea="modules[module][fieldName].textarea"
          />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'integer'">
          <int-field
            v-for="lang in langsListFor(module, fieldName)"
            :key="module + '.' + fieldName + '.' + lang"
            v-model="structure[module][fieldName].models[lang]"
            :prepend-icon="prepend(module, fieldName, lang)"
            :rules="modules[module][fieldName].rules"
            :extend-rules="modules[module][fieldName].extendRules"
            :label="label(module, fieldName, lang)"
            :hint="hint(module, fieldName, lang)"
            :not-null="modules[module][fieldName].notNull"
          />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'textHtml'">
          <html-field
            v-for="lang in langsListFor(module, fieldName)"
            :key="module + '.' + fieldName + '.' + lang"
            :prepend-icon="prepend(module, fieldName, lang)"
            :rules="modules[module][fieldName].rules"
            :label="label(module, fieldName, lang)"
            hide-details="auto"
            v-model="structure[module][fieldName].models[lang]"
            :toolbar="modules[module][fieldName].toolbar"
            :no-br-hint="modules[module][fieldName].noBrHint"
            :max-height="modules[module][fieldName].maxHeight"
          />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'image'">
          <string-image
            v-for="lang in langsListFor(module, fieldName)"
            v-model="structure[module][fieldName].models[lang]"
            :key="module + '.' + fieldName + '.' + lang"
            :size="modules[module][fieldName].size"
            :label="label(module, fieldName, lang)"
            :hint="hint(module, fieldName, lang)"
            :prepend-icon="prepend(module, fieldName, lang, null)"
          />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'divider'">
          <v-divider class="mt-2 mb-4" />
        </template>
        <template v-else-if="modules[module][fieldName].type === 'subheader'">
          <h2
            v-for="lang in langsListFor(module, fieldName)"
            :key="lang"
            class="textCard__subheader my-4"
          >
            {{ label(module, fieldName, lang) }}
          </h2>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import StringImage from "./StringImage";
import { VueEditor } from "vue2-editor";
import StringField from "./StringField";
import HtmlField from "./HtmlField";
import IntField from "./IntField";

export default {
  name: "FieldList",
  components: { HtmlField, StringField, StringImage, IntField, VueEditor },
  props: {
    langs: {
      type: [String, Array],
      default: "auto"
    },
    structure: {
      type: Object,
      required: true
    },
    modules: {
      type: Object,
      required: true
    },
    fieldsSource: {
      type: Object,
      required: true
    },
    unLangPrepend: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      langIcons: {
        ru: "🇷🇺",
        kk: "🇰🇿",
        en: "🇬🇧",
        all: "mdi-translate",
        any: "?"
      },
      langLabels: {
        ru: "рус.",
        kk: "каз.",
        en: "англ.",
        all: "все яз.",
        any: "любой яз."
      },
      imgModels: {}
    };
  },
  methods: {
    langsListFor(module, fieldName) {
      if (this.langs === "auto") {
        return this.modules[module][fieldName].langs;
      }
      const langs = Array.isArray(this.langs) ? this.langs : [this.langs];
      return this.matchedLangs(langs, module, fieldName);
    },
    matchedLangs(langList, module, fieldName) {
      return this.modules[module][fieldName].langs.filter((moduleLang) =>
        langList.includes(moduleLang)
      );
    },
    prepend(module, fieldName, lang, defaultVal = "lang") {
      const data = this.modules[module][fieldName];
      let prepend;
      if (typeof data.prepend === "undefined") prepend = defaultVal;
      else prepend = data.prepend;
      if (prepend === "lang") {
        if (this.unLangPrepend) return null;
        if (this.langIcons[lang]) return this.langIcons[lang];
        return lang;
      } else return data.prepend;
    },
    textareaRules(module, fieldName) {
      const data = this.modules[module][fieldName];
      if (typeof data.rules === "undefined") {
        return [
          (v) => !v || v.length <= 16777215 || "Достигнут максимальный размер"
        ];
      }
      return data.rules;
    },
    textareaRows(module, fieldName) {
      if (typeof this.modules[module][fieldName].rows === "undefined") return 5;
      else return this.modules[module][fieldName].rows;
    },
    label(module, fieldName, lang) {
      const data = this.modules[module][fieldName];
      if (typeof data.label === "object") {
        return data.label[lang];
      } else if (data.langLabel) {
        if (data.label) return data.label + ", " + this.langLabels[lang];
        else return this.langLabels[lang];
      } else return data.label;
    },
    hint(module, fieldName, lang) {
      const data = this.modules[module][fieldName];
      if (typeof data.hint === "object") {
        return data.hint[lang];
      } else if (typeof data.hint !== "undefined") {
        return data.hint;
      } else return null;
    }
  }
};
</script>

<style lang="scss">
.textCard__textarea {
  font-size: 16px;
  font-weight: 300;
  color: #6f6d7a;
  line-height: 1.2;
  min-height: 110px;
}
</style>
